import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { api } from "../services/api";
import {
  ShieldCheck,
  Lock,
  Truck,
  CreditCard,
  QrCode,
  CheckCircle2,
  Sparkles,
  ArrowRight,
  Copy,
  Check,
} from "lucide-react";
import { toast } from "sonner";

export default function CheckoutPage() {
  const { user } = useAuth();
  const { cart, fetchCart } = useCart();
  const navigate = useNavigate();

  const [step, setStep] = useState(1); // 1: Address/Shipping, 2: Payment, 3: Success
  const [shippingService, setShippingService] = useState("SEDEX");
  const [paymentMethod, setPaymentMethod] = useState("pix");
  const [installments, setInstallments] = useState(1);
  const [loading, setLoading] = useState(false);
  const [copiedPix, setCopiedPix] = useState(false);

  // Address form
  const [address, setAddress] = useState({
    recipient_name: user ? user.name : "Cliente Joyce",
    phone: "(11) 98888-7777",
    zip_code: "01310-100",
    street: "Av. Paulista",
    number: "1000",
    complement: "Apto 1502",
    neighborhood: "Bela Vista",
    city: "São Paulo",
    state: "SP",
  });

  // Order result state
  const [orderResult, setOrderResult] = useState(null);

  useEffect(() => {
    if (user && user.name && address.recipient_name === "Cliente Joyce") {
      setAddress((prev) => ({ ...prev, recipient_name: user.name }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Shipping cost calculation
  const cleanCep = address.zip_code.replace(/\D/g, "");
  const firstDigit = parseInt(cleanCep[0] || "0", 10);
  const pacPriceCents = cart.subtotal_in_cents >= 29900 ? 0 : 2200 + (firstDigit * 150);
  const sedexPriceCents = 3500 + (firstDigit * 250);

  const selectedShippingCents = shippingService === "PAC" ? pacPriceCents : sedexPriceCents;
  const totalCents = cart.subtotal_in_cents + selectedShippingCents;

  const handleCreateOrder = async () => {
    if (!user) {
      toast.error("Por favor, faça login ou autentique-se via SSO joycesxy.com antes de finalizar.");
      return;
    }

    if (cart.items.length === 0) {
      toast.error("Seu carrinho está vazio.");
      navigate("/");
      return;
    }

    setLoading(true);
    try {
      const idempotencyKey = `idemp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      
      const payload = {
        shipping_address: address,
        shipping_service: shippingService,
        payment_method: paymentMethod,
        installments: paymentMethod === "credit_card" ? installments : 1,
      };

      const res = await api.post("/checkout/create-order", payload, {
        headers: {
          "Idempotency-Key": idempotencyKey,
        },
      });

      if (res.data.success) {
        setOrderResult(res.data);
        setStep(3);
        await fetchCart();
        toast.success("Pedido criado com sucesso!");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao gerar pedido.");
    } finally {
      setLoading(false);
    }
  };

  const handleSimulateWebhookApproval = async () => {
    if (!orderResult) return;
    setLoading(true);
    try {
      const webhookPayload = {
        event_id: `evt_test_${Date.now()}`,
        event_type: "charge.paid",
        charge_id: `ch_${Date.now()}`,
        order_id: orderResult.order_id,
        amount_in_cents: orderResult.total_in_cents,
        currency: "BRL",
        timestamp: new Date().toISOString(),
      };

      await api.post("/webhooks/infinitepay", webhookPayload);
      toast.success("Webhook InfinitePay: Pagamento Aprovado e Estoque Atualizado!");
      setOrderResult((prev) => ({ ...prev, payment_status: "paid" }));
    } catch (err) {
      toast.error("Erro ao simular webhook.");
    } finally {
      setLoading(false);
    }
  };

  const copyPixCode = () => {
    if (orderResult?.pix_copy_paste) {
      navigator.clipboard.writeText(orderResult.pix_copy_paste);
      setCopiedPix(true);
      toast.success("Código Pix Copia e Cola copiado!");
      setTimeout(() => setCopiedPix(false), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white flex flex-col justify-between">
      <div>
        <Header />

        <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          
          {/* Breadcrumb / Step Indicator */}
          <div className="mb-8">
            <h1 className="font-heading text-3xl font-bold text-white">
              Finalização de Pedido
            </h1>
            <p className="text-xs text-[#A1A1AA] mt-1">
              Pagamento seguro com garantia de entrega e reserva automática de estoque.
            </p>

            <div className="flex items-center gap-4 mt-6 text-xs font-bold">
              <span className={`flex items-center gap-2 ${step >= 1 ? "text-[#FF8C69]" : "text-[#A1A1AA]/40"}`}>
                <span className="w-6 h-6 rounded-xl bg-[#121214] border border-current flex items-center justify-center">1</span>
                <span>Entrega Correios</span>
              </span>
              <span className="text-white/20">———</span>
              <span className={`flex items-center gap-2 ${step >= 2 ? "text-[#FF8C69]" : "text-[#A1A1AA]/40"}`}>
                <span className="w-6 h-6 rounded-xl bg-[#121214] border border-current flex items-center justify-center">2</span>
                <span>Pagamento InfinitePay</span>
              </span>
              <span className="text-white/20">———</span>
              <span className={`flex items-center gap-2 ${step === 3 ? "text-[#10B981]" : "text-[#A1A1AA]/40"}`}>
                <span className="w-6 h-6 rounded-xl bg-[#121214] border border-current flex items-center justify-center">3</span>
                <span>Confirmação</span>
              </span>
            </div>
          </div>

          {/* Step 3: Success Confirmation View */}
          {step === 3 && orderResult ? (
            <div className="max-w-2xl mx-auto rounded-3xl bg-[#121214] border border-white/10 shadow-2xl p-8 text-center space-y-6 animate-in zoom-in-95">
              
              <div className="w-16 h-16 rounded-2xl bg-[#10B981]/20 border border-[#10B981]/40 mx-auto flex items-center justify-center text-[#10B981]">
                <CheckCircle2 className="w-10 h-10" />
              </div>

              <div>
                <span className="inline-block px-3.5 py-1 rounded-full bg-[#18181B] text-xs font-bold text-[#FF8C69] border border-white/10 mb-2 font-mono">
                  Pedido #{orderResult.public_order_number}
                </span>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white">
                  Pedido Realizado com Sucesso!
                </h2>
                <p className="text-xs text-[#A1A1AA] mt-2">
                  Seus itens foram reservados com segurança no sistema.
                </p>
              </div>

              {/* Status Box */}
              <div className="p-4 rounded-2xl bg-[#18181B] border border-white/10 text-left space-y-3 text-xs">
                <div className="flex justify-between items-center pb-2 border-b border-white/10">
                  <span className="text-[#A1A1AA]">Status do Pagamento:</span>
                  <span className={`font-bold uppercase ${orderResult.payment_status === "paid" ? "text-[#10B981]" : "text-[#FF8C69]"}`}>
                    {orderResult.payment_status === "paid" ? "Aprovado / Pago" : "Aguardando Pagamento"}
                  </span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-white/10">
                  <span className="text-[#A1A1AA]">Rastreamento Correios:</span>
                  <span className="font-mono font-bold text-white">{orderResult.tracking_code}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#A1A1AA]">Valor Total:</span>
                  <span className="font-heading text-lg font-bold text-[#FF8C69]">{orderResult.formatted_total}</span>
                </div>
              </div>

              {/* Pix QR Code Mock */}
              {orderResult.payment_method === "pix" && orderResult.payment_status !== "paid" && (
                <div className="p-6 rounded-2xl bg-[#18181B] border border-white/10 space-y-4">
                  <div className="flex items-center justify-center gap-2 text-xs text-[#FF8C69] font-bold">
                    <QrCode className="w-4 h-4" />
                    <span>Pague com Pix Instantâneo InfinitePay</span>
                  </div>

                  <div className="w-48 h-48 bg-white p-3 rounded-2xl mx-auto flex items-center justify-center shadow-md">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(orderResult.pix_qr_code || "pix_sample")}`}
                      alt="QR Code Pix InfinitePay"
                      className="w-full h-full"
                    />
                  </div>

                  <button
                    onClick={copyPixCode}
                    data-testid="btn-copy-pix"
                    className="w-full py-3 rounded-xl bg-[#121214] border border-white/10 hover:border-[#FF8C69]/50 text-xs font-bold text-[#FF8C69] flex items-center justify-center gap-2 transition-colors"
                  >
                    {copiedPix ? <Check className="w-4 h-4 text-[#10B981]" /> : <Copy className="w-4 h-4" />}
                    <span>{copiedPix ? "Código Pix Copiado!" : "Copiar Código Pix Copia e Cola"}</span>
                  </button>
                </div>
              )}

              <div className="flex gap-4">
                <Link
                  to="/pedidos"
                  data-testid="link-view-orders-after-checkout"
                  className="flex-1 py-3.5 rounded-xl bg-[#FF8C69] hover:bg-[#FFA285] text-xs font-bold text-[#0A0A0B] text-center transition-colors"
                >
                  Ver Meus Pedidos
                </Link>
                <Link
                  to="/"
                  className="px-6 py-3.5 rounded-xl bg-[#18181B] border border-white/10 text-xs font-bold text-white hover:border-[#FF8C69] text-center transition-colors"
                >
                  Voltar à Loja
                </Link>
              </div>

            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              
              {/* Left Form: Steps 1 & 2 */}
              <div className="lg:col-span-7 space-y-6">
                
                {/* Step 1: Address & Shipping Selection */}
                {step === 1 && (
                  <div className="rounded-3xl bg-[#121214] border border-white/10 p-6 sm:p-8 space-y-6">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-[#18181B] border border-white/10 flex items-center justify-center text-[#FF8C69]">
                        <Truck className="w-5 h-5" />
                      </div>
                      <div>
                        <h2 className="font-heading text-lg font-bold">1. Endereço e Frete Correios</h2>
                        <p className="text-xs text-[#A1A1AA]">Entrega para todo o Brasil em embalagem discreta</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                      <div className="sm:col-span-2">
                        <label className="block text-[#A1A1AA] mb-1 font-bold">Nome Completo</label>
                        <input
                          type="text"
                          value={address.recipient_name}
                          onChange={(e) => setAddress({ ...address, recipient_name: e.target.value })}
                          data-testid="input-recipient-name"
                          className="w-full bg-[#18181B] border border-white/10 rounded-xl p-3 text-xs text-white focus:outline-none focus:border-[#FF8C69]"
                        />
                      </div>

                      <div>
                        <label className="block text-[#A1A1AA] mb-1 font-bold">Telefone / WhatsApp</label>
                        <input
                          type="text"
                          value={address.phone}
                          onChange={(e) => setAddress({ ...address, phone: e.target.value })}
                          data-testid="input-recipient-phone"
                          className="w-full bg-[#18181B] border border-white/10 rounded-xl p-3 text-xs text-white focus:outline-none focus:border-[#FF8C69]"
                        />
                      </div>

                      <div>
                        <label className="block text-[#A1A1AA] mb-1 font-bold">CEP</label>
                        <input
                          type="text"
                          value={address.zip_code}
                          onChange={(e) => setAddress({ ...address, zip_code: e.target.value })}
                          data-testid="input-recipient-zip"
                          className="w-full bg-[#18181B] border border-white/10 rounded-xl p-3 text-xs text-white focus:outline-none focus:border-[#FF8C69]"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label className="block text-[#A1A1AA] mb-1 font-bold">Endereço (Rua/Avenida)</label>
                        <input
                          type="text"
                          value={address.street}
                          onChange={(e) => setAddress({ ...address, street: e.target.value })}
                          data-testid="input-recipient-street"
                          className="w-full bg-[#18181B] border border-white/10 rounded-xl p-3 text-xs text-white focus:outline-none focus:border-[#FF8C69]"
                        />
                      </div>

                      <div>
                        <label className="block text-[#A1A1AA] mb-1 font-bold">Número</label>
                        <input
                          type="text"
                          value={address.number}
                          onChange={(e) => setAddress({ ...address, number: e.target.value })}
                          data-testid="input-recipient-number"
                          className="w-full bg-[#18181B] border border-white/10 rounded-xl p-3 text-xs text-white focus:outline-none focus:border-[#FF8C69]"
                        />
                      </div>

                      <div>
                        <label className="block text-[#A1A1AA] mb-1 font-bold">Complemento</label>
                        <input
                          type="text"
                          value={address.complement}
                          onChange={(e) => setAddress({ ...address, complement: e.target.value })}
                          data-testid="input-recipient-complement"
                          className="w-full bg-[#18181B] border border-white/10 rounded-xl p-3 text-xs text-white focus:outline-none focus:border-[#FF8C69]"
                        />
                      </div>

                      <div>
                        <label className="block text-[#A1A1AA] mb-1 font-bold">Cidade</label>
                        <input
                          type="text"
                          value={address.city}
                          onChange={(e) => setAddress({ ...address, city: e.target.value })}
                          data-testid="input-recipient-city"
                          className="w-full bg-[#18181B] border border-white/10 rounded-xl p-3 text-xs text-white focus:outline-none focus:border-[#FF8C69]"
                        />
                      </div>

                      <div>
                        <label className="block text-[#A1A1AA] mb-1 font-bold">Estado (UF)</label>
                        <input
                          type="text"
                          value={address.state}
                          onChange={(e) => setAddress({ ...address, state: e.target.value })}
                          data-testid="input-recipient-state"
                          className="w-full bg-[#18181B] border border-white/10 rounded-xl p-3 text-xs text-white focus:outline-none focus:border-[#FF8C69]"
                        />
                      </div>
                    </div>

                    {/* Shipping Modal Selection */}
                    <div className="space-y-3 pt-4 border-t border-white/10">
                      <span className="text-xs font-bold text-white">Escolha o serviço Correios:</span>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <label
                          data-testid="shipping-opt-sedex"
                          className={`p-4 rounded-2xl border cursor-pointer flex flex-col justify-between transition-all ${
                            shippingService === "SEDEX"
                              ? "bg-[#18181B] border-[#FF8C69] shadow-lg"
                              : "bg-[#121214] border-white/10"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-heading font-bold text-sm text-white">SEDEX Expresso</span>
                            <input
                              type="radio"
                              name="shipping"
                              checked={shippingService === "SEDEX"}
                              onChange={() => setShippingService("SEDEX")}
                              className="accent-[#FF8C69]"
                            />
                          </div>
                          <p className="text-[11px] text-[#A1A1AA] mt-2">1 a 3 dias úteis</p>
                          <span className="font-heading font-bold text-[#FF8C69] text-base mt-2">
                            R$ {(sedexPriceCents / 100).toFixed(2).replace(".", ",")}
                          </span>
                        </label>

                        <label
                          data-testid="shipping-opt-pac"
                          className={`p-4 rounded-2xl border cursor-pointer flex flex-col justify-between transition-all ${
                            shippingService === "PAC"
                              ? "bg-[#18181B] border-[#FF8C69] shadow-lg"
                              : "bg-[#121214] border-white/10"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-heading font-bold text-sm text-white">PAC Econômico</span>
                            <input
                              type="radio"
                              name="shipping"
                              checked={shippingService === "PAC"}
                              onChange={() => setShippingService("PAC")}
                              className="accent-[#FF8C69]"
                            />
                          </div>
                          <p className="text-[11px] text-[#A1A1AA] mt-2">4 a 7 dias úteis</p>
                          <span className="font-heading font-bold text-[#FF8C69] text-base mt-2">
                            {pacPriceCents === 0 ? "GRÁTIS" : `R$ ${(pacPriceCents / 100).toFixed(2).replace(".", ",")}`}
                          </span>
                        </label>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => setStep(2)}
                      data-testid="btn-step1-next"
                      className="w-full py-4 rounded-xl bg-[#FF8C69] hover:bg-[#FFA285] text-[#0A0A0B] text-xs font-bold tracking-wider uppercase shadow-lg flex items-center justify-center gap-2"
                    >
                      <span>Avançar para Pagamento</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>

                  </div>
                )}

                {/* Step 2: Payment InfinitePay */}
                {step === 2 && (
                  <div className="rounded-3xl bg-[#121214] border border-white/10 p-6 sm:p-8 space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-[#18181B] border border-white/10 flex items-center justify-center text-[#FF8C69]">
                          <CreditCard className="w-5 h-5" />
                        </div>
                        <div>
                          <h2 className="font-heading text-lg font-bold">2. Forma de Pagamento</h2>
                          <p className="text-xs text-[#A1A1AA]">Processamento seguro InfinitePay</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setStep(1)}
                        className="text-xs text-[#FF8C69] hover:underline font-bold"
                      >
                        Alterar Endereço
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => setPaymentMethod("pix")}
                        data-testid="pay-method-pix"
                        className={`p-5 rounded-2xl border text-left transition-all ${
                          paymentMethod === "pix"
                            ? "bg-[#18181B] border-[#FF8C69] shadow-lg"
                            : "bg-[#121214] border-white/10"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <QrCode className="w-6 h-6 text-[#10B981]" />
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#10B981]/20 text-[#10B981] font-bold">
                            Aprovação Imediata
                          </span>
                        </div>
                        <h4 className="font-heading font-bold text-base text-white mt-3">Pix InfinitePay</h4>
                        <p className="text-[11px] text-[#A1A1AA] mt-1">Gere o QR Code e pague pelo app do seu banco</p>
                      </button>

                      <button
                        type="button"
                        onClick={() => setPaymentMethod("credit_card")}
                        data-testid="pay-method-card"
                        className={`p-5 rounded-2xl border text-left transition-all ${
                          paymentMethod === "credit_card"
                            ? "bg-[#18181B] border-[#FF8C69] shadow-lg"
                            : "bg-[#121214] border-white/10"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <CreditCard className="w-6 h-6 text-[#FF8C69]" />
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FF8C69]/20 text-[#FF8C69] font-bold">
                            Até 12x
                          </span>
                        </div>
                        <h4 className="font-heading font-bold text-base text-white mt-3">Cartão de Crédito</h4>
                        <p className="text-[11px] text-[#A1A1AA] mt-1">Visa, Mastercard, Elo, Hipercard, Amex</p>
                      </button>
                    </div>

                    {paymentMethod === "credit_card" && (
                      <div className="space-y-4 p-4 rounded-2xl bg-[#18181B] border border-white/10 text-xs">
                        <div>
                          <label className="block text-[#A1A1AA] mb-1 font-bold">Opções de Parcelamento:</label>
                          <select
                            value={installments}
                            onChange={(e) => setInstallments(parseInt(e.target.value, 10))}
                            data-testid="select-card-installments"
                            className="w-full bg-[#121214] border border-white/10 rounded-xl p-3 text-xs text-white focus:outline-none focus:border-[#FF8C69]"
                          >
                            <option value="1">1x de R$ {(totalCents / 100).toFixed(2).replace(".", ",")} à vista</option>
                            <option value="2">2x de R$ {(totalCents / 200).toFixed(2).replace(".", ",")}</option>
                            <option value="3">3x de R$ {(totalCents / 300).toFixed(2).replace(".", ",")}</option>
                            <option value="6">6x de R$ {(totalCents / 600).toFixed(2).replace(".", ",")}</option>
                            <option value="12">12x de R$ {(totalCents / 1200).toFixed(2).replace(".", ",")}</option>
                          </select>
                        </div>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={handleCreateOrder}
                      disabled={loading}
                      data-testid="btn-finish-order-submit"
                      className="w-full py-4 rounded-xl bg-[#FF8C69] hover:bg-[#FFA285] text-[#0A0A0B] text-xs font-bold tracking-wider uppercase shadow-lg flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                    >
                      <Lock className="w-4 h-4" />
                      <span>{loading ? "Processando..." : "Confirmar Pedido"}</span>
                    </button>

                  </div>
                )}

              </div>

              {/* Right Summary Column */}
              <div className="lg:col-span-5 rounded-3xl bg-[#121214] border border-white/10 p-6 sm:p-8 space-y-6">
                <h3 className="font-heading text-lg font-bold border-b border-white/10 pb-4">
                  Resumo do Pedido
                </h3>

                {/* Items List */}
                <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
                  {cart.items.map((item) => (
                    <div key={item.product_id} className="flex items-center gap-3 text-xs">
                      <img
                        src={item.main_media_url}
                        alt={item.name}
                        className="w-12 h-14 object-cover rounded-xl bg-[#18181B]"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-white truncate">{item.name}</p>
                        <p className="text-[11px] text-[#A1A1AA]">Qtd: {item.quantity}</p>
                      </div>
                      <span className="font-heading font-bold text-[#FF8C69]">
                        {item.formatted_item_total}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Pricing Totals */}
                <div className="space-y-2 pt-4 border-t border-white/10 text-xs text-[#A1A1AA]">
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <strong className="text-white">{cart.formatted_subtotal}</strong>
                  </div>
                  <div className="flex justify-between">
                    <span>Frete ({shippingService}):</span>
                    <strong className="text-white">
                      {selectedShippingCents === 0 ? "GRÁTIS" : `R$ ${(selectedShippingCents / 100).toFixed(2).replace(".", ",")}`}
                    </strong>
                  </div>
                  <div className="flex justify-between text-base pt-3 border-t border-white/10">
                    <span className="font-bold text-white">Total:</span>
                    <span className="font-heading font-bold text-2xl text-[#FF8C69]">
                      R$ {(totalCents / 100).toFixed(2).replace(".", ",")}
                    </span>
                  </div>
                </div>

                <div className="p-3.5 rounded-2xl bg-[#18181B] border border-white/10 flex items-center gap-3 text-xs text-[#A1A1AA]">
                  <ShieldCheck className="w-5 h-5 text-[#FF8C69] flex-shrink-0" />
                  <span>Compra protegida com entrega garantida e nota de envio.</span>
                </div>

              </div>

            </div>
          )}

        </main>
      </div>

      <Footer />
    </div>
  );
}
